/**
 * useDeepCompareMemo - useMemo with deep value comparison
 * Prevents unnecessary recalculations when Convex queries return new references with identical values
 *
 * CRITICAL FIX (2025-10-22): Improved array handling for Convex pagination objects
 * - Fast path for array length comparison
 * - Safe handling of circular references (WebSocket objects)
 * - Graceful degradation if JSON.stringify fails
 */

import { useRef, DependencyList } from 'react'

function deepCompare(a: any, b: any): boolean {
  // Fast path: reference equality (same object)
  if (a === b) return true

  // Handle null/undefined cases
  if (a == null || b == null) return a === b

  // Handle arrays (common case for Convex .results)
  if (Array.isArray(a) && Array.isArray(b)) {
    // Fast path: different lengths = not equal
    if (a.length !== b.length) return false

    // Empty arrays are equal
    if (a.length === 0) return true

    // Compare array contents by stringifying
    // This handles Convex objects safely
    try {
      return JSON.stringify(a) === JSON.stringify(b)
    } catch (error) {
      // If JSON.stringify fails (circular refs in WebSocket objects),
      // assume arrays are different to be safe
      // This prevents returning stale cached values
      if (import.meta.env.DEV) {
        console.warn(
          '[useDeepCompare] JSON.stringify failed for array comparison, forcing re-render:',
          error
        )
      }
      return false
    }
  }

  // Handle objects
  if (typeof a === 'object' && typeof b === 'object') {
    try {
      return JSON.stringify(a) === JSON.stringify(b)
    } catch (error) {
      // JSON.stringify failed - assume different
      if (import.meta.env.DEV) {
        console.warn(
          '[useDeepCompare] JSON.stringify failed for object comparison:',
          error
        )
      }
      return false
    }
  }

  // Primitives (string, number, boolean)
  return a === b
}

export function useDeepCompareMemo<T>(
  factory: () => T,
  dependencies: DependencyList
): T {
  const ref = useRef<{ deps: DependencyList; value: T }>()

  if (ref.current && deepCompare(ref.current.deps, dependencies)) {
    return ref.current.value
  }

  const value = factory()
  ref.current = { deps: dependencies, value }
  return value
}
